import type { TFunction } from "i18next";
import { ROUTER_PHASES } from "./flows";
import type { FlowNodeDef, UseCaseFlowDef } from "./types";

export type InternalStep = {
  id: string;
  label: string;
  detail: string;
};

function kindStep(t: TFunction, kind: string, key: "a" | "b" | "c"): InternalStep | null {
  const labelKey = `docs.useCaseFlows.kindSteps.${kind}.${key}.label`;
  const detailKey = `docs.useCaseFlows.kindSteps.${kind}.${key}.detail`;
  // Backward-compatible: old flat string keys
  const flatKey = `docs.useCaseFlows.kindSteps.${kind}.${key}`;
  const labelObj = t(labelKey, { defaultValue: "" });
  const label =
    labelObj && labelObj !== labelKey
      ? labelObj
      : (() => {
          const flat = t(flatKey, { defaultValue: "" });
          return flat && flat !== flatKey ? flat : "";
        })();
  if (!label) return null;
  const detailObj = t(detailKey, { defaultValue: "" });
  const detail =
    detailObj && detailObj !== detailKey
      ? detailObj
      : t("docs.useCaseFlows.internalStepFallback", { step: label });
  return { id: `${kind}-${key}`, label, detail };
}

/** Resolve mini-step boxes shown in the expand callout / right panel. */
export function getInternalSteps(
  flow: UseCaseFlowDef,
  node: FlowNodeDef,
  t: TFunction
): InternalStep[] {
  if (node.showRouter) {
    return ROUTER_PHASES.map((phase) => ({
      id: phase.id,
      label: t(`docs.useCaseFlows.router.phases.${phase.labelKey}`),
      detail: t(`docs.useCaseFlows.router.phases.${phase.detailKey}`),
    }));
  }

  const custom = t(`docs.useCaseFlows.details.${flow.id}.${node.id}Steps`, {
    returnObjects: true,
    defaultValue: [],
  });
  if (Array.isArray(custom) && custom.length > 0) {
    const first = custom[0];
    if (typeof first === "string") {
      const nodeDetail = t(`docs.useCaseFlows.details.${flow.id}.${node.id}`, {
        defaultValue: t(`docs.useCaseFlows.details.${flow.id}.fallback`),
      });
      return (custom as string[]).map((label, i) => ({
        id: `${node.id}-s${i}`,
        label,
        detail: t("docs.useCaseFlows.internalStepFallback", {
          step: label,
          defaultValue: nodeDetail,
        }),
      }));
    }
    if (typeof first === "object" && first !== null) {
      return (custom as { label?: string; detail?: string }[])
        .filter((s) => s.label)
        .map((s, i) => ({
          id: `${node.id}-s${i}`,
          label: String(s.label),
          detail:
            s.detail ||
            t("docs.useCaseFlows.internalStepFallback", { step: s.label }),
        }));
    }
  }

  const fromEdges = flow.edges
    .filter((e) => e.from === node.id && e.labelKey)
    .map((e, i) => {
      const label = t(`docs.useCaseFlows.edges.${e.labelKey}`);
      const edgeDetailKey = `docs.useCaseFlows.edgeDetails.${e.labelKey}`;
      const edgeDetail = t(edgeDetailKey, { defaultValue: "" });
      return {
        id: `${e.from}-${e.to}-${i}`,
        label,
        detail:
          edgeDetail && edgeDetail !== edgeDetailKey
            ? edgeDetail
            : t("docs.useCaseFlows.internalStepFallback", { step: label }),
      };
    });

  const kindSteps = (["a", "b", "c"] as const)
    .map((k) => kindStep(t, node.kind, k))
    .filter((x): x is InternalStep => Boolean(x));

  if (fromEdges.length > 0) {
    return [...kindSteps.slice(0, 1), ...fromEdges, ...kindSteps.slice(1, 2)].slice(0, 5);
  }

  if (kindSteps.length > 0) return kindSteps;

  const label = t(`docs.useCaseFlows.nodes.${node.labelKey}`);
  return [
    {
      id: `${node.id}-detail`,
      label,
      detail: t(`docs.useCaseFlows.details.${flow.id}.${node.id}`, {
        defaultValue: t(`docs.useCaseFlows.details.${flow.id}.fallback`),
      }),
    },
  ];
}
